import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api';
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { toJalaali, toGregorian } from 'jalaali-js';
// --- Jalali (Shamsi) date helpers using jalaali-js library ---
const _pad2 = (n: number) => String(n).padStart(2, '0');
const _toEnDigits = (s: string) =>
  s
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
const _parseJalali = (inStr: string) => {
  if (!inStr) return null;
  const s = _toEnDigits(inStr.trim()).replace(/-/g, '/').replace(/\./g, '/');
  const m = s.match(/^(\d{4})[\/ ](\d{1,2})[\/ ](\d{1,2})$/);
  if (!m) return null;
  const jy = parseInt(m[1], 10);
  const jm = parseInt(m[2], 10);
  const jd = parseInt(m[3], 10);
  if (jm < 1 || jm > 12 || jd < 1 || jd > 31) return null;
  return { jy, jm, jd };
};
// Convert Gregorian date to Shamsi string using jalaali-js library
const gregorianToShamsiString = (date: Date | string): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  const { jy, jm, jd } = toJalaali(d.getFullYear(), d.getMonth() + 1, d.getDate());
  return `${jy}/${_pad2(jm)}/${_pad2(jd)}`;
};
// Format Jalali input string (YYYY/MM/DD) -> Gregorian 'YYYY-MM-DD' using jalaali-js library
const jalaliInputToGregorianISO = (inStr: string): string | '' => {
  const pj = _parseJalali(inStr);
  if (!pj) return '';
  const { gy, gm, gd } = toGregorian(pj.jy, pj.jm, pj.jd);
  return `${gy}-${_pad2(gm)}-${_pad2(gd)}`;
};

// Insurance cache for resolved names
const insuranceNameCache = new Map<string, string>();
const supplementaryInsuranceNameCache = new Map<string, string>();

// Insurance name resolution with fallback for deleted insurances
const resolveInsuranceName = async (insuranceId: string): Promise<string> => {
  if (!insuranceId || insuranceId === 'none') return 'هیچکدام';

  // Check cache first
  if (insuranceNameCache.has(insuranceId)) {
    return insuranceNameCache.get(insuranceId) || 'حذف شده';
  }

  try {
    const response = await fetch(`${API_CONFIG.baseURL}/insurances/details/${insuranceId}`, {
      headers: { Authorization: localStorage.getItem('authToken') ? `Bearer ${localStorage.getItem('authToken')}` : '' }
    });

    if (response.ok) {
      const result = await response.json();
      if (result.success && result.data) {
        const name = result.data.name;
        insuranceNameCache.set(insuranceId, name);
        return name;
      }
    }

    // If insurance not found or inactive, cache and return "حذف شده"
    insuranceNameCache.set(insuranceId, 'حذف شده');
    return 'حذف شده';
  } catch (error) {

    insuranceNameCache.set(insuranceId, 'حذف شده');
    return 'حذف شده';
  }
};

// Supplementary insurance name resolution with fallback for deleted insurances
const resolveSupplementaryInsuranceName = async (insuranceId: string): Promise<string> => {
  if (!insuranceId || insuranceId === 'none') return 'هیچکدام';

  // Check cache first
  if (supplementaryInsuranceNameCache.has(insuranceId)) {
    return supplementaryInsuranceNameCache.get(insuranceId) || 'حذف شده';
  }

  try {
    const response = await fetch(`${API_CONFIG.baseURL}/supplementary-insurances/details/${insuranceId}`, {
      headers: { Authorization: localStorage.getItem('authToken') ? `Bearer ${localStorage.getItem('authToken')}` : '' }
    });

    if (response.ok) {
      const result = await response.json();
      if (result.success && result.data) {
        const name = result.data.name;
        supplementaryInsuranceNameCache.set(insuranceId, name);
        return name;
      }
    }

    // If insurance not found or inactive, cache and return "حذف شده"
    supplementaryInsuranceNameCache.set(insuranceId, 'حذف شده');
    return 'حذف شده';
  } catch (error) {

    supplementaryInsuranceNameCache.set(insuranceId, 'حذف شده');
    return 'حذف شده';
  }
};

// Synchronous version for field mappers (returns ID if not cached)
const getInsuranceNameSync = (insuranceId: string): string => {
  if (!insuranceId || insuranceId === 'none') return 'هیچکدام';
  return insuranceNameCache.get(insuranceId) || insuranceId; // Return ID as fallback
};

// Synchronous version for supplementary insurance field mappers
const getSupplementaryInsuranceNameSync = (insuranceId: string): string => {
  if (!insuranceId || insuranceId === 'none') return 'هیچکدام';
  return supplementaryInsuranceNameCache.get(insuranceId) || insuranceId; // Return ID as fallback
};

// Translation switch functions
const translatePatientType = (type: string): string => {
  const translations: Record<string, string> = {
    'self': 'خودم',
    'child': 'فرزند',
    'spouse': 'همسر',
    'father': 'پدر',
    'mother': 'مادر',
    'other': 'سایر'
  };
  return translations[type] || type;
};

const translateGender = (gender: string): string => {
  const translations: Record<string, string> = {
    'male': 'مرد',
    'female': 'زن'
  };
  return translations[gender] || gender;
};

const translateStatus = (status: string, request?: any): string => {
  if (status === 'paid') {
    // Check if total amount (base + transportation) is 0 OR no transaction exists
    if (request) {
      const baseAmount = request.amount || 0;
      const transportationAmount = request.transportationPrice || 0;
      const totalAmount = baseAmount + transportationAmount;

      if (totalAmount === 0 || !request.transaction) {
        return 'نهایی شده';
      }
    }
    return 'نهایی و پرداخت شده';
  }

  const translations: Record<string, string> = {
    'incomplete': 'ناتمام',
    'canceled': 'لغو شده',
    'reviewing': 'در حال بررسی',
    'approved': 'تایید شده',
    'rejected': 'تایید نشده',
    'edited': 'ویرایش شده',
    'expired': 'منقضی شده'
  };
  return translations[status] || status;
};

const translateSamplingLocationType = (type: string): string => {
  const translations: Record<string, string> = {
    'home': 'منزل',
    'lab': 'آزمایشگاه'
  };
  return translations[type] || type;
};

const translateInsuranceType = (type: string): string => {
  const translations: Record<string, string> = {
    'free': 'آزاد',
    'paper': 'دفترچه بیمه',
    'electronic': 'نسخه الکترونیک',
    'package': 'پکیج آزمایشگاه'
  };
  return translations[type] || type;
};

const translateImageReportStatus = (status: string): string => {
  const translations: Record<string, string> = {
    'pending': 'در حال بررسی',
    'approved': 'تایید شده',
    'rejected': 'تایید نشده'
  };
  return translations[status] || status;
};

const translateBankPaymentStatus = (status: string): string => {
  const translations: Record<string, string> = {
    'pending': 'در حال بررسی',
    'approved': 'تایید شده',
    'rejected': 'تایید نشده',
    'no_payment_needed': 'عدم نیاز به پرداخت'
  };
  return translations[status] || status;
};

// Report list (display text in Persian). The numeric id is used to request/generate the report.
// Report numbers (keep in sync with backend/report definitions):
// 1 گزارش درخواست های لغو شدە
// 2 گزارش درخواست های تایید نشده به همراه علل عدم تایید
// 3 گزارش پرداخت های نهایی شده و پرداخت شده
// 4 گزارش درخواست های ناتمام
// 5 گزارش درخواست های منقضی شده
// 6 گزارش تعداد ثبت نامها در روز
// 7 گزارش درخواست های ثبت شده در ساعت مورد نظر
// 8 گزارش درخواست های پاسخ داده شده در ساعت مورد نظر
// 9 گزارش کلیه درخواستهای آزمایش
// 10 گزارش درخواستهایی که بیمار آن کودکان زیر 5 سال است
// 11 گزارش به تفکیک آدرس محل انجام
// 12 گزارش به تفکیک انجام آزمایش در منزل و در آزمایشگاه
// 13 گزارش بر اساس زمان ثبت درخواست
// 14 گزارش بر اساس زمان پرداخت
// 15 گزارش بر اساس درگاه پرداخت
// 16 گزارش به تفکیک تعداد ایجاد درخواست
// 17 گزارش به تفکیک تعداد پاسخگویی به درخواست
// 18 گزارش به تفکیک بیمه های پایه و تکمیلی
// 19 گزارش بیمه های تعریف شده
// 20 گزارش دستورالعمل های تعریف شده
// 21 گزارش دلایل عدم تایید تعریف شده
// 22 گزارش دلایل عدم تایید تعریف شده بخش دریافت جواب آزمایش
// 23 گزارش درخواستهای باقیمانده بر اساس زمان ثبت
// 24 گزارش درخواستهای باقیمانده بر اساس درگاه پرداخت
// 25 گزارش کامل درخواست های باقیمانده
// 26 گزارش کامل درخواست دریافت pdf جواب آزمایش
// 27 گزارش درخواست های ویرایش شده
// 28 گزارش درخواست های در حال بررسی
// 29

const REPORT_OPTIONS: { id: number; label: string }[] = [
  { id: 1, label: '1 گزارش درخواست های لغو شدە' },
  { id: 2, label: '2 گزارش درخواست های تایید نشده به همراه علل عدم تایید' },
  { id: 3, label: '3 گزارش درخواست های نهایی شده و پرداخت شده' },
  { id: 4, label: '4 گزارش درخواست های ناتمام' },
  { id: 5, label: '5 گزارش درخواست های منقضی شده' },
  { id: 6, label: '6 گزارش تعداد ثبت نامها در روز' },
  { id: 7, label: '7 گزارش درخواست های ثبت شده در ساعت مورد نظر' },
  { id: 8, label: '8 گزارش درخواست های پاسخ داده شده در ساعت مورد نظر' },
  { id: 9, label: '9 گزارش کلیه درخواستهای آزمایش' },
  { id: 10, label: '10 گزارش درخواستهایی که بیمار آن کودکان زیر 5 سال است' },
  { id: 11, label: '11 گزارش به تفکیک آدرس محل انجام' },
  { id: 12, label: '12 گزارش به تفکیک انجام آزمایش در منزل و در آزمایشگاه' },
  { id: 13, label: '13 گزارش بر اساس زمان ثبت درخواست' },
  { id: 14, label: '14 گزارش بر اساس زمان پرداخت' },
  { id: 15, label: '15 گزارش بر اساس درگاه پرداخت' },
  { id: 16, label: '16 گزارش به تفکیک تعداد ایجاد درخواست' },
  { id: 17, label: '17 گزارش به تفکیک تعداد پاسخگویی به درخواست' },
  { id: 18, label: '18 گزارش به تفکیک بیمه های پایه و تکمیلی' },
  { id: 19, label: '19 گزارش بیمه های تعریف شده' },
  { id: 20, label: '20 گزارش دستورالعمل های تعریف شده' },
  { id: 21, label: '21 گزارش دلایل عدم تایید تعریف شده' },
  { id: 22, label: '22 گزارش دلایل عدم تایید تعریف شده بخش دریافت جواب آزمایش' },
  { id: 23, label: '23 گزارش همه درخواستهای باقیمانده (فیلتر زمان ثبت)' },
  { id: 24, label: '24 گزارش پرداختی‌های موفق باقیمانده (فیلتر زمان پرداخت)' },
  { id: 25, label: '25 گزارش کامل باقیمانده (پرداخت شده و نشده)' },
  { id: 26, label: '26 گزارش کامل درخواست دریافت pdf جواب آزمایش' },
  { id: 27, label: '27 گزارش درخواست های ویرایش شده' },
  { id: 28, label: '28 گزارش درخواست های تایید شده' },
];

type TableData = {
  columns: string[];
  rows: Record<string, any>[];
};

// Field labels map (index -> label). Keep in sync with the provided field list.
const FIELD_LABELS: Record<number, string> = {
  1: 'کدپیگیری',
  2: 'نام کامل کاربر',
  3: 'کدملی کاربر',
  4: 'شماره (شناسه رهگیری)',
  5: 'نام مراجعه کننده',
  6: 'نام خانوادگی مراجعه کننده',
  7: 'کدملی مراجعه کننده',
  8: 'نسبت مراجعه کننده و کاربر',
  9: 'شماره تماس',
  10: 'جنسیت مراجعه کننده',
  11: 'سن مراجعه کننده',
  12: 'بیمه پایه مراجعه کننده',
  13: 'بیمه تکمیلی مراجعه کننده',
  14: 'تاریخ ثبت نام مراجعه کننده',
  15: 'ساعت ثبت نام مراجعه کننده',
  16: 'تاریخ ثبت درخواست مراجعه کننده',
  17: 'ساعت ثبت درخواست مراجعه کننده',
  18: 'تاریخ پاسخ آزمایشگاه',
  19: 'ساعت پاسخ آزمایشگاه',
  20: 'نام فرد پاسخگوی درخواست آزمایش',
  21: 'تاریخ ویرایش درخواست توسط مراجعه کننده',
  22: 'ساعت ویرایش درخواست توسط مراجعه کننده',
  23: 'تاریخ تکمیل درخواست توسط مراجعه کننده',
  24: 'ساعت تکمیل درخواست توسط مراجعه کننده',
  25: 'تاریخ نهایی سازی درخواست توسط مراجعه کننده',
  26: 'ساعت نهایی سازی درخواست توسط مراجعه کننده',
  27: 'تاریخ حذف درخواست توسط کاربر',
  28: 'هزینه اعلام شده (ریال)',
  29: 'هزینه ایاب و ذهاب',
  30: 'هزینه پرداخت شده (ریال)',
  31: 'تاریخ پرداخت توسط مراجعه کننده',
  32: 'منجر به پرداخت ناموفق',
  33: 'تعداد تکرار ناموفق',
  34: 'محل نمونه گیری در',
  35: 'منجر به پرداخت موفق',
  36: 'کد رهگیری درگاه',
  37: 'بدون هزینه (پرداخت با تکمیلی یا تخفیف)',
  38: 'نام درگاه پرداخت',
  39: '', // Removed: 'پرداخت از طریق (سایت یا اپلیکیشن)'
  40: 'دستورالعمل اعلام شده به مراجعه کننده',
  41: 'دلایل عدم تایید اعلام شده به مراجعه کننده',
  42: 'تاریخ ثبت درخواست باقیمانده',
  43: 'ساعت ثبت درخواست باقیمانده',
  44: 'تاریخ پاسخ آزمایشگاه به درخواست باقیمانده',
  45: 'ساعت پاسخ آزمایشگاه به درخواست باقیمانده',
  46: 'تاریخ ویرایش درخواست باقیمانده',
  47: 'ساعت ویرایش درخواست باقیمانده',
  48: 'نام فرد پاسخگوی درخواست باقیمانده',
  49: 'تاریخ پرداخت باقیمانده',
  50: 'ساعت پرداخت باقیمانده',
  51: 'نام درگاه پرداخت باقیمانده',
  52: 'کد رهگیری درگاه باقیمانده',
  53: '', // Removed: 'پرداخت از طریق (سایت یا اپلیکیشن) باقیمانده'
  54: 'تاریخ ثبت درخواست تصویر جواب آزمایش',
  55: 'ساعت ثبت درخواست جواب آزمایش',
  56: 'تاریخ پاسخ آزمایشگاه به درخواست جواب آزمایش',
  57: 'ساعت پاسخ آزمایشگاه به درخواست جواب آزمایش',
  58: 'تاریخ ویرایش درخواست جواب آزمایش',
  59: 'ساعت ویرایش درخواست جواب آزمایش',
  60: 'نام فرد پاسخگوی درخواست جواب آزمایش',
  61: 'تاریخ مشاهده جواب آزمایش',
  62: 'ساعت مشاهده جواب آزمایش',
  63: 'تاریخ دانلود جواب آزمایش',
  64: 'ساعت دانلود جواب آزمایش',
  65: 'وضعیت درخواست',
  66: 'آدرس محل نمونه گیری',
  67: 'تغییر مکان نمونه گیری به دلایل بازه سنی زیر ۵ سال',
  68: 'نام بیمه پایه - نیاز به دفترچه - کد رهگیری - وضعیت فعالسازی',
  69: 'نام بیمه تکمیلی - وضعیت فعالسازی',
  70: 'دستورالعمل های درخواست آزمایش',
  71: 'دلایل عدم تایید درخواست آزمایش',
  72: 'دلایل عدم تایید دریافت تصویر جواب آزمایش',
};

// Report -> field id list mapping (from user)
const REPORT_COLUMNS: Record<number, number[]> = {
  1: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 41, 65],
  2: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 41, 65],
  3: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29, 30, 31, 32, 33, 35, 36, 37, 38, 40, 41, 65, 66],
  4: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 34, 65],
  5: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29, 30, 31, 32, 33, 35, 36, 37, 38, 40, 41, 65, 66],
  6: [1, 3, 4, 9, 14, 15, 65],
  7: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29, 30, 31, 32, 33, 35, 36, 37, 38, 40, 41, 65, 66, 67],
  8: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29, 30, 31, 32, 33, 35, 36, 37, 38, 40, 41, 65, 66, 67],
  9: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29, 30, 31, 32, 33, 35, 36, 37, 38, 40, 41, 65, 66, 67],
  10: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29, 30, 31, 32, 33, 35, 36, 37, 38, 40, 41, 65, 66, 67],
  11: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29, 30, 31, 32, 33, 35, 36, 37, 38, 40, 41, 65, 66, 67],
  12: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29, 30, 31, 32, 33, 35, 36, 37, 38, 40, 41, 65, 66, 67],
  13: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29, 30, 31, 32, 33, 35, 36, 37, 38, 40, 41, 65, 66],
  14: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17, 18, 19, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 40, 41, 65, 66],
  15: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17, 18, 19, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 40, 41, 65, 66],
  16: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17, 18, 19, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 40, 41, 65, 66],
  17: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17, 18, 19, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 40, 41, 65, 66],
  18: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29, 30, 31, 32, 33, 35, 36, 37, 38, 40, 41, 65, 66, 67],
  19: [1, 68, 69],
  20: [1, 70],
  21: [1, 71],
  22: [1, 72],
  23: [1, 3, 4, 5, 6, 7, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52],
  24: [1, 3, 4, 5, 6, 7, 42, 43, 44, 45, 49, 50, 51, 52],
  25: [1, 3, 4, 5, 6, 7, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52],
  26: [1, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64],
  27: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23],
  28: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 21, 22, 23],
};

// Will be filled dynamically after each load
// Map report id to backend collection kind: 'test' | 'user' | 'bank' | 'image' | 'commands' | 'insurances'
const getReportKind = (id: number): 'test' | 'user' | 'bank' | 'image' | 'commands' | 'insurances' => {
  if (id === 6) return 'user';
  if (id === 19) return 'insurances';
  if (id === 20 || id === 21 || id === 22) return 'commands';
  if (id === 23 || id === 24 || id === 25) return 'bank';
  if (id === 26) return 'image';
  return 'test';
};

const dtStr = (d: any) => {
  if (!d) return '';
  const t = new Date(d);
  if (isNaN(t.getTime())) return '';
  return t.toISOString().slice(0, 10);
};
const tmStr = (d: any) => {
  if (!d) return '';
  const t = new Date(d);
  if (isNaN(t.getTime())) return '';
  return t.toTimeString().slice(0, 5);
};

// === TestRequest field mapper by numeric id ===
const mapField_Test = (r: any, id: number, transportationPrice: number = 0): any => {
  switch (id) {
    case 1: return r.displayId ?? r._id ?? '';
    case 2: return r.userId ? `${r.userId?.name ?? ''} ${r.userId?.lastName ?? ''}`.trim() : '';
    case 3: return r.patientInfo?.nationalId ?? r.userId?.nationalId ?? '';
    case 4: return r.displayId ?? r._id ?? '';
    case 5: return r.patientInfo?.firstName ?? '';
    case 6: return r.patientInfo?.lastName ?? '';
    case 7: return r.patientInfo?.nationalId ?? '';
    case 8: return translatePatientType(r.patientInfo?.patientType ?? '');
    case 9: return r.patientInfo?.mobile ?? r.userId?.mobile ?? '';
    case 10: return translateGender(r.patientInfo?.gender ?? '');
    case 11: return r.ageInfo?.age ?? '';
    case 12: return getInsuranceNameSync(r.insuranceInfo?.basicInsurance ?? '');
    case 13: return getSupplementaryInsuranceNameSync(r.insuranceInfo?.supplementaryInsurance ?? '');
    case 14: return r.userId?.createdAt ? gregorianToShamsiString(r.userId.createdAt) : '';
    case 15: return r.userId?.createdAt ? tmStr(r.userId.createdAt) : '';
    case 16: return gregorianToShamsiString(r.createdAt);
    case 17: return tmStr(r.createdAt);
    case 18: return gregorianToShamsiString(r.labResponseDateTime);
    case 19: return tmStr(r.labResponseDateTime);
    case 20: return r.adminId ? `${r.adminId?.name ?? ''} ${r.adminId?.lastName ?? ''}`.trim() : '';
    case 21: return gregorianToShamsiString(r.continueDateTime);
    case 22: return tmStr(r.continueDateTime);
    case 23: return gregorianToShamsiString(r.paymentDateTime);
    case 24: return tmStr(r.paymentDateTime);
    case 25: return gregorianToShamsiString(r.paymentDateTime);
    case 26: return tmStr(r.paymentDateTime);
    case 27: return ''; // null
    case 28: return r.amount ?? '';
    case 29:
      // Transportation cost calculation
      // Use saved transportationPrice if available, otherwise calculate from settings
      if (r.transportationPrice !== undefined && r.transportationPrice !== null) {
        return r.transportationPrice === 0
          ? 'رایگان'
          : `${new Intl.NumberFormat('fa-IR').format(r.transportationPrice)} ریال`;
      }
      // Fallback to calculation based on sampling location
      if (r.samplingLocation?.freeTransportation === true) {
        return 'رایگان';
      }
      if (r.samplingLocation?.type === 'lab') {
        return 'رایگان'; // No transportation cost for lab sampling
      }
      if (r.samplingLocation?.type === 'home' && transportationPrice > 0) {
        return `${new Intl.NumberFormat('fa-IR').format(transportationPrice)} ریال`;
      }
      return '-';
    case 30:
      // Total paid amount = base amount + transportation cost
      const baseAmount = r.amount ?? 0;
      let transportCost = 0;

      // Use saved transportationPrice if available
      if (r.transportationPrice !== undefined && r.transportationPrice !== null) {
        transportCost = r.transportationPrice;
      } else {
        // Calculate transportation cost from settings
        if (r.samplingLocation?.type === 'home' &&
          r.samplingLocation?.freeTransportation !== true &&
          transportationPrice > 0) {
          transportCost = transportationPrice;
        }
      }
      // For lab sampling or free transportation, transportCost remains 0

      const totalAmount = baseAmount + transportCost;
      return totalAmount > 0 ? totalAmount : '';
    case 31: return gregorianToShamsiString(r.paymentDateTime);
    case 32: return r.transaction?.status === 'failed' ? 'بله' : ''; // Failed payment
    case 33: return ''; // null
    case 34: return translateSamplingLocationType(r.samplingLocation?.type ?? '');
    case 35: return r.transaction?.status === 'completed' ? 'بله' : ''; // Successful payment
    case 36: return r.transaction?.refId ?? ''; // Gateway tracking code
    case 37: return ''; // null
    case 38:
      // Gateway name (similar to CompletedRequests.tsx)
      if (r.transaction?.status === 'completed') {
        const gateway = r.transaction?.gateway;
        return gateway === 'parsian' ? 'پارسیان' : gateway || '---';
      }
      return '---';
    case 39: return ''; // Removed: 'پرداخت از طریق (سایت یا اپلیکیشن)'
    case 40: return r.instructions ?? '';
    case 41: return r.rejectionReason ?? '';
    case 65: return translateStatus(r.status ?? '', r);
    case 66: return r.addressInfo?.street ? `${r.addressInfo?.region ?? ''} ${r.addressInfo?.field ?? ''} ${r.addressInfo?.street ?? ''}`.trim() : '';
    case 67: return r.ageInfo?.age != null && r.ageInfo.age < 5 ? 'true' : '';
    default: return r[String(id)] ?? '';
  }
};

// === BankPaymentRequest field mapper (id 42..53 plus 1,3,4,5,6,7) ===
const mapField_Bank = (r: any, id: number): any => {
  switch (id) {
    case 1: return r.displayId ?? r._id ?? '';
    case 3: return r.patientInfo?.nationalId ?? '';
    case 4: return r.displayId ?? r._id ?? '';
    case 5: return r.patientInfo?.firstName ?? '';
    case 6: return r.patientInfo?.lastName ?? '';
    case 7: return r.patientInfo?.nationalId ?? '';
    case 42: return gregorianToShamsiString(r.createdAt);
    case 43: return tmStr(r.createdAt);
    case 44: return ''; // (image report date, not in bank)
    case 45: return ''; // (image report time)
    case 46: return gregorianToShamsiString(r.adminResponse?.responseDate);
    case 47: return tmStr(r.adminResponse?.responseDate);
    case 48: return r.adminResponse?.respondedBy ? `${r.adminResponse.respondedBy?.name ?? ''} ${r.adminResponse.respondedBy?.lastName ?? ''}`.trim() : '';
    case 49: return gregorianToShamsiString(r.transaction?.paymentDate); // تاریخ پرداخت باقیمانده
    case 50: return tmStr(r.transaction?.paymentDate); // ساعت پرداخت باقیمانده
    case 51:
      // نام درگاه پرداخت باقیمانده
      // Check transaction first, then fallback to paymentInfo
      if (r.transaction?.gateway) {
        return r.transaction.gateway === 'parsian' ? 'پارسیان' :
          r.transaction.gateway === 'zarinpal' ? 'زرین‌پال' :
            r.transaction.gateway;
      }
      // Fallback to paymentInfo if transaction doesn't have gateway
      if (r.paymentInfo?.gateway) {
        return r.paymentInfo.gateway === 'parsian' ? 'پارسیان' :
          r.paymentInfo.gateway === 'zarinpal' ? 'زرین‌پال' :
            r.paymentInfo.gateway;
      }
      return '';
    case 52: return r.transaction?.refId ?? ''; // کد رهگیری درگاه باقیمانده
    case 53: return ''; // Removed: 'پرداخت از طریق (سایت یا اپلیکیشن) باقیمانده'
    default: return r[String(id)] ?? '';
  }
};

// === ImageReportRequest field mapper (id 54..64 plus 1) ===
const mapField_Image = (r: any, id: number): any => {
  switch (id) {
    case 1: return r.displayId ?? r._id ?? '';
    case 54: return gregorianToShamsiString(r.createdAt);
    case 55: return tmStr(r.createdAt);
    case 56: return gregorianToShamsiString(r.adminResponse?.responseDate);
    case 57: return tmStr(r.adminResponse?.responseDate);
    case 58: return gregorianToShamsiString(r.updatedAt);
    case 59: return tmStr(r.updatedAt);
    case 60: return r.adminResponse?.respondedBy ? `${r.adminResponse.respondedBy?.name ?? ''} ${r.adminResponse.respondedBy?.lastName ?? ''}`.trim() : '';
    case 61:
    case 62:
    case 63:
    case 64:
      return '';
    default: return r[String(id)] ?? '';
  }
};

// Generate sample rows with keys that match column labels
const generateSampleRows = (columns: string[], count = 5): Record<string, any>[] => {
  return Array.from({ length: count }).map((_, i) => {
    const row: Record<string, any> = {};
    columns.forEach((col) => {
      // create realistic value based on column label content
      if (col.includes('کدپیگیری') || col.includes('کد رهگیری')) row[col] = `R-${1000 + i}`;
      else if (col.includes('نام ') || col.includes('نام کاربر') || col.includes('نام مراجعه')) row[col] = i === 0 ? 'علی' : `کاربر ${i}`;
      else if (col.includes('کدملی')) row[col] = `00${100000000 + i}`;
      else if (col.includes('شماره تماس')) row[col] = `09${10000000 + i}`;
      else if (col.includes('تاریخ') && !col.includes('ساعت')) row[col] = `2025-08-0${(i % 9) + 1}`;
      else if (col.includes('ساعت')) {
        // produce HH:MM
        const hh = String((8 + i) % 24).padStart(2, '0');
        const mm = String((10 + i * 7) % 60).padStart(2, '0');
        row[col] = `${hh}:${mm}`;
      } else if (col.includes('هزینه') || col.includes('پرداخت')) row[col] = 250000 + i * 10000;
      else if (col.includes('درگاه')) {
        const DEMO_GATEWAYS = ['زرین‌پال', 'ملت', 'سامان'];
        row[col] = DEMO_GATEWAYS[i % DEMO_GATEWAYS.length];
      }
      else if (col.includes('وضعیت')) row[col] = i % 2 === 0 ? 'تکمیل' : 'در حال بررسی';
      else row[col] = `نمونه ${i + 1}`;
    });
    return row;
  });
};

// Helper to generate small sample data for each report option.
// In future we'll replace this with actual API calls. Keep report id references
// so backend integration is straightforward.
// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return { headers: { Authorization: token ? `Bearer ${token}` : '' } };
};

// Preload insurance names for all insurance IDs found in the data
const preloadInsuranceNames = async (data: any[]): Promise<void> => {
  const basicInsuranceIds = new Set<string>();
  const supplementaryInsuranceIds = new Set<string>();

  // Collect all unique insurance IDs from the data
  data.forEach((item: any) => {
    if (item.insuranceInfo?.basicInsurance) {
      basicInsuranceIds.add(item.insuranceInfo.basicInsurance);
    }
    if (item.insuranceInfo?.supplementaryInsurance) {
      supplementaryInsuranceIds.add(item.insuranceInfo.supplementaryInsurance);
    }
  });

  // Resolve all insurance names in parallel
  const basicPromises = Array.from(basicInsuranceIds).map(id => resolveInsuranceName(id));
  const supplementaryPromises = Array.from(supplementaryInsuranceIds).map(id => resolveSupplementaryInsuranceName(id));

  await Promise.all([...basicPromises, ...supplementaryPromises]);
};

// Fetch real data for the selected report (server-side, real collections)
const fetchReportData = async (reportId: number, params: any = {}, page: number = 1) => {
  const API_URL = API_CONFIG.baseURL;
  const mergedParams = { ...(params || {}), page, limit: params?.limit ?? 20 };

  try {
    const kind = getReportKind(reportId);

    // Helper to compute columns from numeric ids
    const fieldIds = REPORT_COLUMNS[reportId] || [1, 2, 3];
    const columns = fieldIds.map((id) => FIELD_LABELS[id] || `field_${id}`);

    if (kind === 'user') {
      // /api/admin/users
      const res = await axios.get(`${API_URL}/admin/users`, { params: mergedParams, ...getAuthHeaders() });
      const users = res.data?.data || [];
      // Preload insurance names for users (if they have insurance data)
      await preloadInsuranceNames(users);

      // Minimal mapping for report 6 (users today) to requested columns
      const rows = users.map((u: any) => {
        const row: Record<string, any> = {};
        fieldIds.forEach((fid, idx) => {
          const label = columns[idx];
          switch (fid) {
            case 1: row[label] = u.displayId || u._id || ''; break;
            case 3: row[label] = u.nationalId || ''; break;
            case 4: row[label] = u.displayId || u._id || ''; break;
            case 9: row[label] = u.mobile || u.phone || ''; break;
            case 14: row[label] = u.createdAt ? gregorianToShamsiString(u.createdAt) : ''; break;
            case 15: row[label] = u.createdAt ? tmStr(u.createdAt) : ''; break;
            case 65: row[label] = translateStatus(u.status || ''); break;
            default: row[label] = u[String(fid)] ?? '';
          }
        });
        return row;
      });
      return { columns, rows, total: res.data?.total || users.length, totalPages: res.data?.totalPages || 1 };
    }

    if (kind === 'bank') {
      // /api/bank-payments/admin/all
      // Pass reportId to backend for proper filtering
      const bankParams = { ...mergedParams, reportId };
      const res = await axios.get(`${API_URL}/bank-payments/admin/all`, { params: bankParams, ...getAuthHeaders() });
      const data = res.data?.data || [];
      const rows = data.map((r: any) => {
        const row: Record<string, any> = {};
        // respondedBy is populated in backend
        fieldIds.forEach((fid, idx) => {
          const label = columns[idx];
          row[label] = mapField_Bank(r, fid);
        });
        return row;
      });

      const pag = res.data?.pagination || {};
      return { columns, rows, total: pag.totalItems || data.length, totalPages: pag.totalPages || 1 };
    }

    if (kind === 'image') {
      // /api/image-reports/admin/all
      const res = await axios.get(`${API_URL}/image-reports/admin/all`, { params: mergedParams, ...getAuthHeaders() });
      const data = res.data?.data || [];
      const rows = data.map((r: any) => {
        const row: Record<string, any> = {};
        fieldIds.forEach((fid, idx) => {
          const label = columns[idx];
          row[label] = mapField_Image(r, fid);
        });
        return row;
      });
      const pag = res.data?.pagination || {};
      return { columns, rows, total: pag.total || data.length, totalPages: pag.totalPages || 1 };
    }

    if (kind === 'insurances') {
      const res = await axios.get(`${API_URL}/insurances`, { ...getAuthHeaders() });
      const ins = res.data?.data || [];
      const rows = ins.map((i: any) => {
        const row: Record<string, any> = {};
        fieldIds.forEach((fid, idx) => {
          const label = columns[idx];
          if (fid === 1) row[label] = i._id;
          else if (fid === 68) row[label] = i.name ?? '';
          else if (fid === 69) row[label] = translateInsuranceType(i.status ?? '');
          else row[label] = i[String(fid)] ?? '';
        });
        return row;
      });
      return { columns, rows, total: ins.length, totalPages: 1 };
    }

    if (kind === 'commands') {
      // /api/admin/commands returns grouped: { instructions, reasons, problems }
      const res = await axios.get(`${API_URL}/admin/commands`, { ...getAuthHeaders() });
      const grouped = res.data || {};
      const items = reportId === 20 ? grouped.instructions : reportId === 21 ? grouped.reasons : grouped.problems;
      const columns2 = ['_id', 'text'];
      const rows = (items || []).map((it: any) => ({ _id: it._id, text: it.text }));
      return { columns: columns2, rows, total: rows.length, totalPages: 1 };
    }

    // === TEST REQUESTS (default) ===
    // Map report ids to status filters supported by /admin/requests
    const statusByReport: Record<number, string | undefined> = {
      1: 'canceled',
      2: 'rejected',
      3: 'paid',
      4: 'incomplete',
      5: 'expired',
      27: 'edited',
      28: 'approved'
    };
    const q: any = { ...mergedParams };
    const status = statusByReport[reportId];
    if (status) q.status = status;

    // Add age filter for report 10 (age < 5)
    if (reportId === 10) {
      q.ageLt = 5;
    }

    const res = await axios.get(`${API_URL}/admin/requests`, { params: q, ...getAuthHeaders() });
    const requests = res.data?.data || [];

    // Client-side additional filters (time window for 7/8, age < 5 for 10)
    let filtered = requests as any[];

    // Age filter for report 10 is now handled server-side

    // Preload insurance names for all requests
    await preloadInsuranceNames(filtered);

    // Fetch transportation price from settings
    let transportationPrice = 0;
    try {
      const settingsRes = await axios.get(`${API_URL}/settings`, { ...getAuthHeaders() });
      if (settingsRes.data?.success) {
        transportationPrice = settingsRes.data.data?.transportationPrice || 0;
      }
    } catch (error) {

      // Continue with default value of 0
    }

    // Build mapped rows
    const rows = filtered.map((r: any) => {
      const row: Record<string, any> = {};
      fieldIds.forEach((fid, idx) => {
        const label = columns[idx];
        row[label] = mapField_Test(r, fid, transportationPrice);
      });
      return row;
    });

    // Pagination info from backend
    const total = res.data?.total ?? res.data?.pagination?.total ?? requests.length;
    const totalPages = res.data?.totalPages ?? res.data?.pagination?.totalPages ?? 1;

    // If no data, reset pagination
    if (rows.length === 0) {
      return { columns, rows: [], total: 0, totalPages: 1 };
    }

    return { columns, rows, total, totalPages };
  } catch (err) {

    const fieldIds = REPORT_COLUMNS[reportId] || [1, 2, 3];
    const columns = fieldIds.map((id) => FIELD_LABELS[id] || `field_${id}`);
    // On error, return empty table and reset pagination
    return { columns, rows: [], total: 0, totalPages: 1 };
  }
};

// Keep a local sample generator for initial state and fallback usage
const getSampleData = (reportId: number): TableData => {
  const fieldIds = REPORT_COLUMNS[reportId] || [1, 2, 3];
  const columns = fieldIds.map((id) => FIELD_LABELS[id] || `field_${id}`);
  const rows = generateSampleRows(columns, 6);
  return { columns, rows };
};
const formatDate = (d: string | Date) => {
  const dt = typeof d === 'string' ? new Date(d) : d;
  return dt.toISOString().slice(0, 10);
};

const exportToCSV = (filename: string, columns: string[], rows: Record<string, any>[]) => {
  if (!columns || columns.length === 0) {
    return;
  }
  const header = columns.join(',') + '\n';
  const body = rows
    .map((row) =>
      columns
        .map((col) => {
          const v = row[col];
          if (v === null || v === undefined) return '';
          // Escape quotes
          const s = String(v).replace(/"/g, '""');
          // Wrap if needed
          return s.includes(',') || s.includes('\n') ? `"${s}"` : s;
        })
        .join(',')
    )
    .join('\n');

  const csv = header + body;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const Reports: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<number>(1);
  const [fromDateShamsi, setFromDateShamsi] = useState<string>('');
  const [toDateShamsi, setToDateShamsi] = useState<string>('');
  const [fromTime, setFromTime] = useState<string>('');
  const [toTime, setToTime] = useState<string>('');
  const [gatewayFilter, setGatewayFilter] = useState<string>('');
  const [table, setTable] = useState<TableData>({ columns: [], rows: [] });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 20; // pagination: 20 items per page
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [gatewayOptions, setGatewayOptions] = useState<string[]>([]);

  // Clear filter functions
  const clearFromDate = () => setFromDateShamsi('');
  const clearToDate = () => setToDateShamsi('');
  const clearFromTime = () => setFromTime('');
  const clearToTime = () => setToTime('');
  const clearGateway = () => setGatewayFilter('');

  // Reset to first page when selected report changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedReport]);

  // Reset pagination when data is empty
  useEffect(() => {
    if (table.rows.length === 0 && currentPage > 1) {
      setCurrentPage(1);
    }
  }, [table.rows.length, currentPage]);

  const pageRows = table.rows;

  // When currentPage changes, fetch server-side page
  useEffect(() => {
    // Only fetch when table.columns is set (i.e., after an initial load)
    const loadPage = async () => {
      if (!table.columns || table.columns.length === 0) return;
      const fromG = fromDateShamsi ? jalaliInputToGregorianISO(fromDateShamsi) : undefined;
      const toG = toDateShamsi ? jalaliInputToGregorianISO(toDateShamsi) : undefined;
      const data = await fetchReportData(
        selectedReport,
        {
          from: fromG,
          to: toG,
          fromTime: (selectedReport === 7 || selectedReport === 8) ? fromTime || undefined : undefined,
          toTime: (selectedReport === 7 || selectedReport === 8) ? toTime || undefined : undefined,
          gateway: (selectedReport === 15 || selectedReport === 24) ? (gatewayFilter || undefined) : undefined,
        },
        currentPage
      );
      setTable({ columns: data.columns, rows: data.rows });
      setTotalItems(data.total || 0);
      setTotalPages(data.totalPages || 1);

      // If no data and we're not on page 1, reset to page 1
      if (data.rows.length === 0 && currentPage > 1) {
        setCurrentPage(1);
      }
      // refresh gateway options for current page
      const gatewayCols = data.columns.filter((c) => c.includes('نام درگاه'));
      const uniq = new Set<string>();
      if (gatewayCols.length) {
        data.rows.forEach((r: any) => {
          gatewayCols.forEach((gc) => {
            const v = String(r[gc] ?? '').trim();
            if (v) uniq.add(v);
          });
        });
      }
      setGatewayOptions(Array.from(uniq));
    };
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const onLoad = async () => {
    const params: any = {};
    const fromG = fromDateShamsi ? jalaliInputToGregorianISO(fromDateShamsi) : '';
    const toG = toDateShamsi ? jalaliInputToGregorianISO(toDateShamsi) : '';
    if (fromG) params.from = fromG;
    if (toG) params.to = toG;
    if (selectedReport === 7 || selectedReport === 8) {
      if (fromTime) params.fromTime = fromTime;
      if (toTime) params.toTime = toTime;
    }
    if (selectedReport === 15 || selectedReport === 24) {
      if (gatewayFilter) params.gateway = gatewayFilter;
    }
    setCurrentPage(1);
    const data = await fetchReportData(selectedReport, params, 1);
    setTable({ columns: data.columns, rows: data.rows });
    setTotalItems(data.total || 0);
    setTotalPages(data.totalPages || 1);

    // If no data, ensure pagination is reset
    if (data.rows.length === 0) {
      setCurrentPage(1);
      setTotalItems(0);
      setTotalPages(1);
    }
    // Build gateway options dynamically from current data
    const gatewayCols = data.columns.filter((c) => c.includes('نام درگاه'));
    const uniq = new Set<string>();
    if (gatewayCols.length) {
      data.rows.forEach((r: any) => {
        gatewayCols.forEach((gc) => {
          const v = String(r[gc] ?? '').trim();
          if (v) uniq.add(v);
        });
      });
    }
    setGatewayOptions(Array.from(uniq));
  };

  const onExport = async () => {
    try {
      const API_URL = API_CONFIG.baseURL;
      const token = localStorage.getItem('authToken');
      const kind = getReportKind(selectedReport);

      // Common params
      const fromG = fromDateShamsi ? jalaliInputToGregorianISO(fromDateShamsi) : '';
      const toG = toDateShamsi ? jalaliInputToGregorianISO(toDateShamsi) : '';

      const authHeaders = { Authorization: token ? `Bearer ${token}` : '' };
      let url = '';
      const params: any = {};

      if (fromG) params.from = fromG;
      if (toG) params.to = toG;

      // Route by report kind
      if (kind === 'test') {
        // TestRequest-based exports (1,2,3,4,5,7,8,9,10,11,12,13,14,15,16,17,18,27,28)
        url = `${API_URL}/admin/requests/export`;
        params.reportId = selectedReport;
        const statusByReport: Record<number, string | undefined> = {
          1: 'canceled',
          2: 'rejected',
          3: 'paid',
          4: 'incomplete',
          5: 'expired',
          27: 'edited',
          28: 'approved'
        };
        const status = statusByReport[selectedReport];
        if (status) params.status = status;
        if (selectedReport === 7 || selectedReport === 8) {
          if (fromTime) params.fromTime = fromTime;
          if (toTime) params.toTime = toTime;
        }
        if (selectedReport === 15) {
          if (gatewayFilter) params.gateway = gatewayFilter;
        }
        // Add age filter for report 10 (age < 5)
        if (selectedReport === 10) {
          params.ageLt = 5;
        }
      } else if (kind === 'user') {
        // Users export (report 6)
        url = `${API_URL}/admin/users/export`;
        // (backend expects from/to on createdAt)
      } else if (kind === 'bank') {
        // Bank payment requests (23–25)
        url = `${API_URL}/bank-payments/admin/export`;
        params.reportId = selectedReport; // Pass reportId for proper filtering
        if (gatewayFilter && selectedReport === 24) params.gateway = gatewayFilter;
      } else if (kind === 'image') {
        // Image report requests (26)
        url = `${API_URL}/image-reports/admin/export`;
      } else if (kind === 'insurances') {
        // Base insurances (19)
        url = `${API_URL}/insurances/export`;
        // Additional filters (search/active) could be added later
      } else if (kind === 'commands') {
        // Commands (20, 21, 22) — export by type
        url = `${API_URL}/admin/commands/export`;
        params.type = selectedReport === 20 ? 'instruction' : selectedReport === 21 ? 'reason' : 'problem';
      }

      if (!url) {
        alert('آدرس سرویس خروجی برای این گزارش تعریف نشده است.');
        return;
      }

      const res = await axios.get(url, { params, responseType: 'blob', headers: authHeaders });

      const blob = new Blob([res.data], {
        type: res.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const link = document.createElement('a');
      const urlObj = URL.createObjectURL(blob);
      link.href = urlObj;
      const fallback = `report-${selectedReport}-${formatDate(new Date())}.xlsx`;
      const filename = res.headers['content-disposition']
        ? res.headers['content-disposition'].split('filename=')[1]?.replace(/"/g, '')
        : fallback;
      link.setAttribute('download', filename || fallback);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(urlObj);
    } catch (err) {

      alert('خطا در دریافت خروجی اکسل از سرور');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">صفحه گزارشات (خروجی)</h1>

      <div className="bg-white p-4 rounded shadow-sm mb-4">
        <div className="flex gap-2 justify-end">
          <button onClick={onLoad} className="bg-blue-600 text-white px-4 py-2 rounded">بارگذاری</button>
          <button onClick={onExport} className="bg-green-600 text-white px-4 py-2 rounded">خروجی اکسل</button>
        </div>
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div>
            <label className="block text-sm text-slate-700 mb-1">انتخاب گزارش</label>
            <select
              value={selectedReport}
              onChange={(e) => setSelectedReport(Number(e.target.value))}
              className="border p-2 rounded"
            >
              {REPORT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-slate-700 mb-1">از تاریخ (شمسی)</label>
            <div className="relative">
              <DatePicker
                value={fromDateShamsi}
                onChange={(d: any) => setFromDateShamsi(d ? (typeof d.format === 'function' ? d.format("YYYY/MM/DD") : String(d)) : "")}
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                inputClass="w-44 px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="از تاریخ را انتخاب کنید"
                format="YYYY/MM/DD"
              />
              {fromDateShamsi && (
                <button
                  type="button"
                  onClick={clearFromDate}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                  title="پاک کردن تاریخ"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-700 mb-1">تا تاریخ (شمسی)</label>
            <div className="relative">
              <DatePicker
                value={toDateShamsi}
                onChange={(d: any) => setToDateShamsi(d ? (typeof d.format === 'function' ? d.format("YYYY/MM/DD") : String(d)) : "")}
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                inputClass="w-44 px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="تا تاریخ را انتخاب کنید"
                format="YYYY/MM/DD"
              />
              {toDateShamsi && (
                <button
                  type="button"
                  onClick={clearToDate}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                  title="پاک کردن تاریخ"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {(selectedReport === 7 || selectedReport === 8) && (
            <>
              <div>
                <label className="block text-sm text-slate-700 mb-1">از ساعت</label>
                <div className="relative">
                  <input
                    type="time"
                    value={fromTime}
                    onChange={(e) => setFromTime(e.target.value)}
                    className="border p-2 pr-8 rounded w-full"
                  />
                  {fromTime && (
                    <button
                      type="button"
                      onClick={clearFromTime}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                      title="پاک کردن ساعت"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-700 mb-1">تا ساعت</label>
                <div className="relative">
                  <input
                    type="time"
                    value={toTime}
                    onChange={(e) => setToTime(e.target.value)}
                    className="border p-2 pr-8 rounded w-full"
                  />
                  {toTime && (
                    <button
                      type="button"
                      onClick={clearToTime}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                      title="پاک کردن ساعت"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {(selectedReport === 15 || selectedReport === 24) && (
            <div>
              <label className="block text-sm text-slate-700 mb-1">درگاه پرداخت</label>
              <div className="relative">
                <select
                  value={gatewayFilter}
                  onChange={(e) => setGatewayFilter(e.target.value)}
                  className="border p-2 pr-8 rounded w-full"
                >
                  <option value="">همه درگاه‌ها</option>
                  {gatewayOptions.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
                {gatewayFilter && (
                  <button
                    type="button"
                    onClick={clearGateway}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                    title="پاک کردن درگاه"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white p-4 rounded shadow-sm">
        <h2 className="text-lg font-medium mb-3">پیش نمایش جدول</h2>
        <div className="overflow-x-auto max-w-full border rounded">
          <table className="min-w-max w-full table-auto border-collapse text-xs md:text-sm">
            <thead>
              <tr>
                {table.columns.map((col) => (
                  <th key={col} className="border-b px-3 py-2 text-left bg-slate-50/90 backdrop-blur sticky top-0 z-10 font-semibold whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? '' : 'bg-slate-50'}>
                  {table.columns.map((col) => (
                    <td key={col} className="border px-3 py-2 align-top">{String(row[col] ?? '')}</td>
                  ))}
                </tr>
              ))}
              {table.rows.length === 0 && (
                <tr>
                  <td colSpan={table.columns.length} className="p-4 text-center text-slate-500">
                    داده‌ای برای نمایش وجود ندارد
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Pagination controls */}
      <div className="flex items-center justify-between mt-3">
        <div className="text-sm text-slate-600">
          {totalItems > 0 ? `نمایش صفحه ${currentPage} از ${totalPages} — کل ${totalItems} مورد` : 'هیچ داده‌ای برای نمایش وجود ندارد'}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1 || totalItems === 0}
            className={`px-3 py-1 rounded ${currentPage === 1 || totalItems === 0 ? 'bg-slate-100 text-slate-400' : 'bg-white border'}`}>
            قبلی
          </button>

          {/* Page numbers - show up to 7 numbers centered around current page */}
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }).map((_, i) => {
              const page = i + 1;
              const showWindow = 7;
              const half = Math.floor(showWindow / 2);
              if (totalPages > showWindow && Math.abs(page - currentPage) > half && page !== 1 && page !== totalPages) {
                if (page === 2 && currentPage - half > 2) return <span key={page} className="px-2">...</span>;
                if (page === totalPages - 1 && currentPage + half < totalPages - 1) return <span key={page} className="px-2">...</span>;
                return null;
              }
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-2 py-1 rounded ${page === currentPage ? 'bg-blue-600 text-white' : 'bg-white border'}`}>
                  {page}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || totalItems === 0}
            className={`px-3 py-1 rounded ${currentPage === totalPages || totalItems === 0 ? 'bg-slate-100 text-slate-400' : 'bg-white border'}`}>
            بعدی
          </button>
        </div>
      </div>
    </div>
  );
};

export default Reports;
